import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import Header from "./Header";
import Footer from "./Footer";
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Wallet,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Info,
  Printer,
} from "lucide-react";
import { colors } from "../config/site";

/**
 * Move-In Reality Check
 * Adult, practical tool that reveals the TRUE upfront cost of moving into
 * an apartment. Complements the existing monthly Budget Calculator by owning
 * the one-time cost problem — the #1 reason people fail their first tenancy.
 */

const currency = (n) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const numOrZero = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const MoveInRealityCheck = () => {
  const [step, setStep] = useState(0); // 0 intro, 1 inputs, 2 results
  const [rent, setRent] = useState("");
  const [savings, setSavings] = useState("");
  const [monthlySave, setMonthlySave] = useState("");
  const [depositMonths, setDepositMonths] = useState("1"); // 1 or 2
  const [needsFurniture, setNeedsFurniture] = useState("some"); // none | some | most
  const [movingStyle, setMovingStyle] = useState("diy"); // diy | help | movers

  const calc = useMemo(() => {
    const r = numOrZero(rent);
    const currentSavings = numOrZero(savings);
    const perMonthSave = numOrZero(monthlySave);
    const depMonths = parseInt(depositMonths, 10) || 1;

    const securityDeposit = r * depMonths;
    const firstMonthRent = r;

    // Utility setup deposits (electric $75, gas $75, internet $50) — realistic PA averages
    const utilitySetup = r > 0 ? 200 : 0;

    // Renters insurance first payment (~$15/mo, often 3 mo upfront)
    const rentersInsurance = r > 0 ? 45 : 0;

    // Moving costs
    const movingCost =
      movingStyle === "movers"
        ? 800
        : movingStyle === "help"
        ? 200
        : r > 0
        ? 75
        : 0;

    // Furniture / essentials
    const furnitureCost =
      needsFurniture === "most"
        ? 1500
        : needsFurniture === "some"
        ? 500
        : 0;

    // Application / credit-check fees (typical range $35–$75)
    const applicationFee = r > 0 ? 50 : 0;

    const total =
      securityDeposit +
      firstMonthRent +
      utilitySetup +
      rentersInsurance +
      movingCost +
      furnitureCost +
      applicationFee;

    const gap = Math.max(0, total - currentSavings);
    const monthsToReady =
      gap > 0 && perMonthSave > 0 ? Math.ceil(gap / perMonthSave) : 0;

    return {
      securityDeposit,
      firstMonthRent,
      utilitySetup,
      rentersInsurance,
      movingCost,
      furnitureCost,
      applicationFee,
      total,
      gap,
      monthsToReady,
      currentSavings,
      perMonthSave,
    };
  }, [rent, savings, monthlySave, depositMonths, needsFurniture, movingStyle]);

  const isReady = calc.total > 0 && calc.currentSavings >= calc.total;
  const isClose =
    !isReady && calc.total > 0 && calc.currentSavings >= calc.total * 0.6;

  const verdict = useMemo(() => {
    if (calc.total === 0) return null;
    if (isReady) {
      return {
        tone: colors.primary,
        label: "You're move-in ready.",
        headline: "Your savings cover every upfront cost. Green light.",
        summary:
          "You have enough to walk into a lease without draining your safety net. Make sure you keep at least one month's expenses untouched as an emergency cushion.",
      };
    }
    if (isClose) {
      return {
        tone: colors.gold,
        label: "You're close — a short runway away.",
        headline: `About ${calc.monthsToReady || "a few"} month${
          calc.monthsToReady === 1 ? "" : "s"
        } of steady saving and you're there.`,
        summary:
          "You're closer than you think. Hold off on signing until your savings cover the full upfront number so month one doesn't wipe you out.",
      };
    }
    return {
      tone: colors.terracotta,
      label: "Not yet — let's build a plan.",
      headline: calc.monthsToReady
        ? `At $${Math.round(
            calc.perMonthSave
          )}/mo saved, you're about ${calc.monthsToReady} months from ready.`
        : "The gap is significant. A savings plan is the next step.",
      summary:
        "Signing a lease before you can cover the upfront cost is the #1 cause of a bad first tenancy. Take the runway to save — your future self will thank you.",
    };
  }, [calc, isReady, isClose]);

  const restart = () => {
    setRent("");
    setSavings("");
    setMonthlySave("");
    setDepositMonths("1");
    setNeedsFurniture("some");
    setMovingStyle("diy");
    setStep(0);
  };

  const canSeeResults = numOrZero(rent) > 0;

  const InputRow = ({ label, prefix, value, onChange, placeholder, testId, hint }) => (
    <div>
      <label
        className="block text-sm font-semibold mb-1.5"
        style={{ color: colors.charcoal }}
      >
        {label}
      </label>
      <div
        className="flex items-center rounded-xl border-2 overflow-hidden focus-within:ring-2"
        style={{ borderColor: "#E6DFD2", backgroundColor: colors.white }}
      >
        {prefix && (
          <span
            className="px-3 py-3 text-sm font-semibold border-r"
            style={{
              borderColor: "#E6DFD2",
              color: colors.textMuted,
              backgroundColor: colors.cream,
            }}
          >
            {prefix}
          </span>
        )}
        <input
          type="number"
          inputMode="decimal"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-grow px-3 py-3 text-base outline-none bg-transparent"
          style={{ color: colors.charcoal }}
          data-testid={testId}
        />
      </div>
      {hint && (
        <p className="text-xs mt-1.5" style={{ color: colors.textMuted }}>
          {hint}
        </p>
      )}
    </div>
  );

  const OptionPill = ({ selected, onClick, label, sub, testId }) => (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl border-2 px-4 py-3 transition-all hover:shadow-sm"
      style={{
        borderColor: selected ? colors.primary : "#E6DFD2",
        backgroundColor: selected ? colors.sage + "22" : colors.white,
      }}
      data-testid={testId}
    >
      <p
        className="text-sm font-semibold"
        style={{ color: colors.charcoal }}
      >
        {label}
      </p>
      {sub && (
        <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
          {sub}
        </p>
      )}
    </button>
  );

  const LineItem = ({ label, value, note }) => (
    <div
      className="flex justify-between items-start py-2.5 border-b"
      style={{ borderColor: "#EFE9DE" }}
    >
      <div className="pr-3">
        <p
          className="text-sm font-semibold"
          style={{ color: colors.charcoal }}
        >
          {label}
        </p>
        {note && (
          <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
            {note}
          </p>
        )}
      </div>
      <p
        className="text-sm font-bold whitespace-nowrap"
        style={{ color: colors.charcoal }}
      >
        {currency(value)}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-28 pb-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <Link
            to="/resources"
            className="inline-flex items-center text-sm font-semibold mb-6"
            style={{ color: colors.primary }}
            data-testid="movein-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Link>

          <div
            className="rounded-3xl shadow-lg overflow-hidden"
            style={{ backgroundColor: colors.white }}
          >
            {/* INTRO */}
            {step === 0 && (
              <div className="p-8 lg:p-12" data-testid="movein-intro">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                  style={{
                    backgroundColor: colors.sage + "33",
                    color: colors.primary,
                  }}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Module 2 · Money Matters
                </div>
                <h1
                  className="text-3xl lg:text-5xl font-bold leading-tight mb-5"
                  style={{ color: colors.charcoal }}
                >
                  Move-In Reality Check
                </h1>
                <p className="text-lg mb-5" style={{ color: colors.textMuted }}>
                  Everyone budgets for rent. Almost no one budgets for the day they sign.
                  This tool shows the <strong>real upfront cost</strong> to move into
                  an apartment — and how close you are to being ready.
                </p>
                <div
                  className="rounded-2xl p-5 mb-7 flex gap-4 items-start"
                  style={{ backgroundColor: colors.ivory }}
                >
                  <Info
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: colors.terracotta }}
                  />
                  <div>
                    <p
                      className="text-xs uppercase tracking-widest font-bold mb-1"
                      style={{ color: colors.terracottaDark }}
                    >
                      Why this matters
                    </p>
                    <p className="text-sm" style={{ color: colors.charcoal }}>
                      Running out of cash in month one is the #1 preventable cause of
                      a shaky first tenancy. Knowing this number before you sign changes everything.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setStep(1)}
                  className="rounded-full px-7 py-6 text-base font-semibold text-white shadow-md hover:shadow-xl transition-all"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="movein-start-btn"
                >
                  Start the Check
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            )}

            {/* INPUTS */}
            {step === 1 && (
              <div className="p-8 lg:p-12" data-testid="movein-inputs">
                <h2
                  className="text-2xl lg:text-3xl font-bold mb-2"
                  style={{ color: colors.charcoal }}
                >
                  Tell us about the apartment
                </h2>
                <p
                  className="text-sm mb-7"
                  style={{ color: colors.textMuted }}
                >
                  Rough estimates are fine. You can change anything and the number updates.
                </p>

                <div className="grid sm:grid-cols-2 gap-5 mb-7">
                  <InputRow
                    label="Target monthly rent"
                    prefix="$"
                    value={rent}
                    onChange={setRent}
                    placeholder="1000"
                    testId="movein-rent"
                    hint="What you expect to pay per month."
                  />
                  <InputRow
                    label="What you have saved right now"
                    prefix="$"
                    value={savings}
                    onChange={setSavings}
                    placeholder="1500"
                    testId="movein-savings"
                    hint="Cash on hand you could use for move-in."
                  />
                  <InputRow
                    label="How much you can save per month"
                    prefix="$"
                    value={monthlySave}
                    onChange={setMonthlySave}
                    placeholder="200"
                    testId="movein-monthly"
                    hint="Realistic amount, not aspirational."
                  />
                  <div>
                    <label
                      className="block text-sm font-semibold mb-1.5"
                      style={{ color: colors.charcoal }}
                    >
                      Security deposit required
                    </label>
                    <div className="flex gap-2">
                      <OptionPill
                        selected={depositMonths === "1"}
                        onClick={() => setDepositMonths("1")}
                        label="1 month"
                        sub="Common"
                        testId="movein-dep-1"
                      />
                      <OptionPill
                        selected={depositMonths === "2"}
                        onClick={() => setDepositMonths("2")}
                        label="2 months"
                        sub="PA legal max"
                        testId="movein-dep-2"
                      />
                    </div>
                    <p
                      className="text-xs mt-1.5"
                      style={{ color: colors.textMuted }}
                    >
                      In PA, deposits can&apos;t exceed 2 months rent in year one.
                    </p>
                  </div>
                </div>

                <div className="mb-7">
                  <p
                    className="text-sm font-semibold mb-2"
                    style={{ color: colors.charcoal }}
                  >
                    Furniture & essentials
                  </p>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <OptionPill
                      selected={needsFurniture === "none"}
                      onClick={() => setNeedsFurniture("none")}
                      label="I have everything"
                      sub="$0"
                      testId="movein-furn-none"
                    />
                    <OptionPill
                      selected={needsFurniture === "some"}
                      onClick={() => setNeedsFurniture("some")}
                      label="A few basics"
                      sub="~$500"
                      testId="movein-furn-some"
                    />
                    <OptionPill
                      selected={needsFurniture === "most"}
                      onClick={() => setNeedsFurniture("most")}
                      label="Starting from scratch"
                      sub="~$1,500"
                      testId="movein-furn-most"
                    />
                  </div>
                </div>

                <div className="mb-8">
                  <p
                    className="text-sm font-semibold mb-2"
                    style={{ color: colors.charcoal }}
                  >
                    How are you moving?
                  </p>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <OptionPill
                      selected={movingStyle === "diy"}
                      onClick={() => setMovingStyle("diy")}
                      label="DIY"
                      sub="Own car / help"
                      testId="movein-move-diy"
                    />
                    <OptionPill
                      selected={movingStyle === "help"}
                      onClick={() => setMovingStyle("help")}
                      label="Rental truck"
                      sub="~$200"
                      testId="movein-move-help"
                    />
                    <OptionPill
                      selected={movingStyle === "movers"}
                      onClick={() => setMovingStyle("movers")}
                      label="Hired movers"
                      sub="~$800"
                      testId="movein-move-movers"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!canSeeResults}
                    className="rounded-full px-7 py-6 font-semibold text-white"
                    style={{ backgroundColor: colors.terracotta }}
                    data-testid="movein-submit-btn"
                  >
                    See My Reality Check
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* RESULTS */}
            {step === 2 && verdict && (
              <div className="p-8 lg:p-12" data-testid="movein-results">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                  style={{
                    backgroundColor: verdict.tone + "22",
                    color: verdict.tone,
                  }}
                >
                  {isReady ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5" />
                  )}
                  {verdict.label}
                </div>
                <h2
                  className="text-2xl lg:text-3xl font-bold mb-2"
                  style={{ color: colors.charcoal }}
                >
                  {verdict.headline}
                </h2>
                <p
                  className="text-sm mb-6"
                  style={{ color: colors.textMuted }}
                >
                  {verdict.summary}
                </p>

                {/* Cost breakdown */}
                <div
                  className="rounded-2xl p-5 mb-6"
                  style={{ backgroundColor: colors.ivory }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Wallet
                      className="w-5 h-5"
                      style={{ color: colors.terracotta }}
                    />
                    <p
                      className="text-xs uppercase tracking-widest font-bold"
                      style={{ color: colors.terracottaDark }}
                    >
                      What move-in actually costs
                    </p>
                  </div>
                  <LineItem
                    label="Security deposit"
                    value={calc.securityDeposit}
                    note={`${depositMonths} × monthly rent`}
                  />
                  <LineItem
                    label="First month's rent"
                    value={calc.firstMonthRent}
                    note="Paid at signing"
                  />
                  <LineItem
                    label="Application & credit-check fees"
                    value={calc.applicationFee}
                    note="Typically $35–$75, non-refundable"
                  />
                  <LineItem
                    label="Utility setup deposits"
                    value={calc.utilitySetup}
                    note="Electric, gas, internet"
                  />
                  <LineItem
                    label="Renters insurance (first payment)"
                    value={calc.rentersInsurance}
                    note="Usually required in the lease"
                  />
                  <LineItem
                    label="Moving costs"
                    value={calc.movingCost}
                    note={
                      movingStyle === "movers"
                        ? "Hired movers"
                        : movingStyle === "help"
                        ? "Rental truck"
                        : "DIY / friends"
                    }
                  />
                  <LineItem
                    label="Furniture & essentials"
                    value={calc.furnitureCost}
                    note={
                      needsFurniture === "most"
                        ? "Starting from scratch"
                        : needsFurniture === "some"
                        ? "A few basics"
                        : "Already have it"
                    }
                  />

                  <div className="flex justify-between items-center pt-4 mt-2">
                    <p
                      className="text-base font-bold"
                      style={{ color: colors.charcoal }}
                    >
                      Total needed before you sign
                    </p>
                    <p
                      className="text-2xl font-bold"
                      style={{ color: colors.primary }}
                      data-testid="movein-total"
                    >
                      {currency(calc.total)}
                    </p>
                  </div>
                </div>

                {/* Runway card */}
                <div
                  className="rounded-2xl p-5 mb-6"
                  style={{
                    backgroundColor: verdict.tone + "15",
                    borderLeft: `4px solid ${verdict.tone}`,
                  }}
                >
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-1"
                        style={{ color: colors.textMuted }}
                      >
                        You have saved
                      </p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: colors.charcoal }}
                        data-testid="movein-have"
                      >
                        {currency(calc.currentSavings)}
                      </p>
                    </div>
                    <div>
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-1"
                        style={{ color: colors.textMuted }}
                      >
                        Gap remaining
                      </p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: verdict.tone }}
                        data-testid="movein-gap"
                      >
                        {currency(calc.gap)}
                      </p>
                    </div>
                    <div>
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-1"
                        style={{ color: colors.textMuted }}
                      >
                        Months to ready
                      </p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: colors.charcoal }}
                        data-testid="movein-months"
                      >
                        {isReady
                          ? "Now"
                          : calc.monthsToReady
                          ? `${calc.monthsToReady} mo`
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Big takeaway CTA */}
                <div
                  className="rounded-3xl p-6 lg:p-7 text-white mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                  }}
                >
                  <h3 className="text-xl lg:text-2xl font-bold mb-2">
                    Next step: check your monthly budget too.
                  </h3>
                  <p className="text-sm opacity-90 mb-5">
                    You&apos;ve covered the day you sign. Now make sure the months after work.
                    The Budget Calculator shows if this rent fits your income long-term.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/resources/renters/calculator"
                      data-testid="movein-cta-calculator"
                    >
                      <Button
                        className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto"
                        style={{
                          backgroundColor: colors.terracotta,
                          color: colors.white,
                        }}
                      >
                        Open Budget Calculator
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to="/contact" data-testid="movein-cta-contact">
                      <Button className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto border-white/40 border bg-transparent hover:bg-white/10">
                        Talk to Our Team
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center">
                  <Button
                    onClick={() => setStep(1)}
                    variant="ghost"
                    className="rounded-full"
                    data-testid="movein-edit-btn"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Edit My Numbers
                  </Button>
                  <Link
                    to={`/tools/move-in-plan?rent=${numOrZero(rent)}&savings=${numOrZero(
                      savings
                    )}&monthly=${numOrZero(
                      monthlySave
                    )}&dep=${depositMonths}&furn=${needsFurniture}&move=${movingStyle}`}
                    data-testid="movein-save-plan"
                  >
                    <Button
                      className="rounded-full px-6 py-5 font-semibold text-white w-full sm:w-auto"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Printer className="mr-2 w-4 h-4" />
                      Save / Print My Plan
                    </Button>
                  </Link>
                  <Button
                    onClick={restart}
                    variant="ghost"
                    className="rounded-full"
                    data-testid="movein-restart-btn"
                  >
                    <RefreshCw className="mr-2 w-4 h-4" />
                    Start Over
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MoveInRealityCheck;
